import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../../hooks/useTheme';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import Search from '../Search/Search';
import { NavItem } from '../../../types';
import styles from './Header.module.css';

// Import logo images
import logoDark from '../../../assets/dark.png';
import logoLight from '../../../assets/light.png';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { theme } = useTheme();
  const location = useLocation();

  // Select logo based on theme
  const currentLogo = theme === 'dark' ? logoDark : logoLight;

  // Navigation Items with Dropdowns
  const navItems: NavItem[] = [
    {
      id: 'home',
      label: 'Home',
      path: '/',
      hasDropdown: false,
    },
    {
      id: 'About',
      label: 'About',
      path: '/about',
      hasDropdown: false,
    },
    {
      id: 'blogs',
      label: 'Blogs',
      path: '/category/trending',
      hasDropdown: true,
      dropdownItems: [
        { id: 'all-blogs', label: 'All Blogs', path: '/blogs' },
        { id: 'featured', label: 'Featured Stories', path: '/category/featured' },
        { id: 'trending', label: 'Trending Now', path: '/category/trending' },
        { id: 'latest', label: 'Latest Posts', path: '/category/trending' }
      ],
    },
    // {
    //   id: 'tech',
    //   label: 'Tech',
    //   path: '/tech',
    //   hasDropdown: true,
    //   dropdownItems: [
    //     { id: 'ai', label: 'Artificial Intelligence', path: '/tech/ai' },
    //     { id: 'blockchain', label: 'Blockchain', path: '/tech/blockchain' },
    //     { id: 'web3', label: 'Web3 & Crypto', path: '/tech/web3' },
    //     { id: 'cloud', label: 'Cloud Computing', path: '/tech/cloud' },
    //     { id: 'cybersecurity', label: 'Cybersecurity', path: '/tech/cybersecurity' },
    //   ],
    // },
    {
      id: 'early-bird', 
      label: 'Early Stage Startups',
      path: '/startups',
      hasDropdown: true,
      dropdownItems: [
        { id: 'seed-stage', label: 'Seed Stage', path: '/category/early-birds-startup' },
        { id: 'series-a', label: 'Series A', path: '/category/early-birds-startup' },
        { id: 'unicorns', label: 'Unicorn Watch', path: '/category/unicorn' },
        { id: 'funding-news', label: 'Funding News', path: '/category/early-birds-startup' },
      ],
    },
    {
      id: 'innovation',
      label: 'Innovation',
      path: '/category/innovation',
      hasDropdown: true,
      dropdownItems: [
        { id: 'research', label: 'Research & Development', path: '/category/innovation' },
        { id: 'breakthroughs', label: 'Breakthroughs', path: '/innovation/breakthroughs' },
        { id: 'farming', label: 'Innovation in farming', path: '/category/farming' },
        { id: 'case-studies', label: 'Case Studies', path: '/category/innovation' },
      ],
    },
    {
      id: 'recognition',
      label: 'Recognition',
      path: '/recognition',
      hasDropdown: true,
      dropdownItems: [
        { id: 'awards', label: 'Awards & Honors', path: '/category/recognition' },
        { id: 'women-empowerment', label: 'Recognition of Women\'s work', path: 'category/women-empowerment' },
        { id: 'success-stories', label: 'Success Stories', path: '/category/recognition' },
        { id: 'hall-of-fame', label: 'Hall of Fame', path: '/category/recognition' },
      ],
    },
  ];

  // Handle Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };

    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [isMobileMenuOpen]);

  const handleDropdownToggle = (navId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === navId ? null : navId);
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles.container}>
          {/* Logo - Updated with Image */}
          <div className={styles.logoSection}>
            <Link to="/" className={styles.logo}>
              <img 
                src={currentLogo} 
                alt="Staruprahi Logo" 
                className={styles.logoImage}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className={styles.nav}>
            <ul className={styles.navList}>
              {navItems.map((item) => (
                <li
                  key={item.id}
                  className={`${styles.navItem} ${isActivePath(item.path) ? styles.active : ''}`}
                >
                  {item.hasDropdown ? (
                    <div className={styles.dropdownWrapper}>
                      <button
                        className={styles.navLink}
                        onClick={(e) => handleDropdownToggle(item.id, e)}
                        aria-expanded={activeDropdown === item.id}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <svg
                          className={`${styles.dropdownIcon} ${
                            activeDropdown === item.id ? styles.rotated : ''
                          }`}
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1.5L6 6.5L11 1.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      {/* Dropdown Menu */}
                      {activeDropdown === item.id && item.dropdownItems && (
                        <div className={styles.dropdown}>
                          <ul className={styles.dropdownList}>
                            {item.dropdownItems.map((dropdownItem) => (
                              <li key={dropdownItem.id} className={styles.dropdownItem}>
                                <Link
                                  to={dropdownItem.path}
                                  className={`${styles.dropdownLink} ${
                                    isActivePath(dropdownItem.path) ? styles.activeDropdown : ''
                                  }`}
                                >
                                  {dropdownItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link to={item.path} className={styles.navLink}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Right Section: Search + Theme Toggle + Subscribe Button */}
          <div className={styles.rightSection}>
            {/* Search Button */}
            <button
              className={styles.searchButton}
              onClick={() => setIsSearchOpen(true)}
              aria-label="Open search"
              title="Search"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M21 21L16.65 16.65"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <ThemeToggle />
            
            <Link to="/subscribe" className={styles.subscribeBtn}>
              Subscribe
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className={styles.mobileMenuToggle}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.active : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className={styles.mobileNav}>
            <ul className={styles.mobileNavList}>
              {navItems.map((item) => (
                <li key={item.id} className={styles.mobileNavItem}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        className={`${styles.mobileNavLink} ${
                          isActivePath(item.path) ? styles.active : ''
                        }`}
                        onClick={(e) => handleDropdownToggle(item.id, e)}
                      >
                        {item.label}
                        <svg
                          className={`${styles.dropdownIcon} ${
                            activeDropdown === item.id ? styles.rotated : ''
                          }`}
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1.5L6 6.5L11 1.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      {activeDropdown === item.id && item.dropdownItems && (
                        <ul className={styles.mobileDropdownList}>
                          {item.dropdownItems.map((dropdownItem) => (
                            <li key={dropdownItem.id}>
                              <Link
                                to={dropdownItem.path}
                                className={`${styles.mobileDropdownLink} ${
                                  isActivePath(dropdownItem.path) ? styles.active : ''
                                }`}
                              >
                                {dropdownItem.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`${styles.mobileNavLink} ${
                        isActivePath(item.path) ? styles.active : ''
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      {/* Search Modal */}
      <Search isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;